import { api } from "../utils/axios";
import type {
  ClaimStartPayload,
  ClaimStartResponse,
  OTPVerifyPayload,
  OTPVerifyResponse,
  ProofUploadResponse,
  DisputeResponse,
  TransferDetails,
  TransferRespondPayload,
  TransferRespondResponse,
  EscalatePayload,
  EscalateResponse,
  TransferStartResponse,
  ClaimerRole,
} from "../types/claim.types";

// ─── Claim: Start ─────────────────────────────────────────────────────────────

export const startClaim = async (
  restaurantId: string,
  payload: ClaimStartPayload,
  token: string
): Promise<ClaimStartResponse> => {
  try {
    const response = await api.post(
      `/api/claims/restaurants/${restaurantId}/claim`,
      payload,
      { withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
       }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to start claim. Please try again."
    );
  }
};

// ─── Claim: Verify OTP ────────────────────────────────────────────────────────

export const verifyClaimOTP = async (
  claimId: string,
  payload: OTPVerifyPayload,
  token: string
): Promise<OTPVerifyResponse> => {
  try {
    const response = await api.post(
      `/api/claims/${claimId}/verify`,
      payload,
      { 
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
       }
    );
    return response.data;
  } catch (error: any) {
    const message: string =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Invalid or expired OTP.";

    // Signal proof fallback to caller
    const isFallback =
      message.toLowerCase().includes("fallback") ||
      message.toLowerCase().includes("proof");

    const err = new Error(message) as Error & { isFallback?: boolean };
    err.isFallback = isFallback;
    throw err;
  }
};

// ─── Claim: Upload Proof ──────────────────────────────────────────────────────

export const uploadClaimProof = async (
  claimId: string,
  file: File,
  token: string,
): Promise<ProofUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post(
      `/api/claims/${claimId}/proof`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}`  },
        withCredentials: true,
        
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to upload proof. Please try again."
    );
  }
};

// ─── Claim: File Dispute ──────────────────────────────────────────────────────

export const fileDispute = async (
  restaurantId: string,
  token: string,
): Promise<DisputeResponse> => {
  try {
    const response = await api.post(
      `/api/claims/restaurants/${restaurantId}/dispute`,
      {},
      { withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
       }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to file dispute. Please try again."
    );
  }
};

// ─── Transfer: Initiate ───────────────────────────────────────────────────────

export const initiateTransfer = async (
  restaurantId: string,
  role: ClaimerRole,
  reason: string,
  file: File,
  token: string
): Promise<TransferStartResponse> => {
  try {
    const formData = new FormData();
    formData.append("role", role);
    formData.append("reason", reason);
    formData.append("file", file);

    const response = await api.post(
      `/api/claims/restaurants/${restaurantId}/transfer`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to initiate transfer. Please try again."
    );
  }
};

// ─── Transfer: Get Details ────────────────────────────────────────────────────

export const getTransferDetails = async (
  transferId: string,
  token: string
): Promise<TransferDetails> => {
  try {
    const response = await api.get(`/api/claims/${transferId}/transfer`, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.transfer;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to load transfer details."
    );
  }
};

// ─── Transfer: Owner Respond ──────────────────────────────────────────────────

export const respondToTransfer = async (
  transferId: string,
  payload: TransferRespondPayload,
  token: string
): Promise<TransferRespondResponse> => {
  try {
    const response = await api.post(
      `/api/claims/${transferId}/transfer/respond`,
      payload,
      { withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
       }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to respond to transfer request."
    );
  }
};

// ─── Transfer: Escalate ───────────────────────────────────────────────────────

export const escalateTransfer = async (
  transferId: string,
  payload: EscalatePayload,
  token: string
): Promise<EscalateResponse> => {
  try {
    const response = await api.post(
      `/api/claims/${transferId}/transfer/escalate`,
      payload ?? {},
      { withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
       }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to escalate transfer."
    );
  }
};